import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatName } from '@/lib/name';
import { getEmailDisplay, type OrganizationMember } from '@/types/user';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useListMembers } from '@/hooks/organization/use-list-members';
import { useActiveAdminChangeProposal } from '@/hooks/admin/organization/use-active-admin-change-proposal';
import type { AdminUserProposal } from '@/hooks/admin/organization/use-active-admin-change-proposal';

type CreateAdminChangeProposal = (arg: {
  organizationId: string;
  adminUserProposals: AdminUserProposal[];
  // Nullable: null means "no change" (keep org's current value).
  numberOfApprovalsNeeded: number | null;
}) => Promise<unknown>;

type Props = {
  organizationId: string | null;
  orgAdminUserIds: string[];
  orgApprovalsNeeded: number;
  isLoadingOrg: boolean;
  createAdminChangeProposal: CreateAdminChangeProposal;
  isCreating: boolean;
};

export function AdminUsersSelector({
  organizationId,
  orgAdminUserIds,
  orgApprovalsNeeded,
  isLoadingOrg,
  createAdminChangeProposal,
  isCreating,
}: Props) {
  const orgAdminUsers = useMemo(() => orgAdminUserIds, [orgAdminUserIds]);
  const { members, isLoading: isLoadingMembers } =
    useListMembers(organizationId);
  const { activeProposal, refresh: refreshActiveProposal } =
    useActiveAdminChangeProposal();

  const [open, setOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [tempAdminUserIds, setTempAdminUserIds] = useState<string[]>(
    () => orgAdminUserIds
  );
  const [tempApprovalsNeeded, setTempApprovalsNeeded] = useState<number>(() => {
    const max = Math.max(1, orgAdminUserIds.length);
    return Math.min(Math.max(1, orgApprovalsNeeded), max);
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const lastSyncedOrgStateKeyRef = useRef<string | null>(null);
  const didUserEditRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    // Each time the dialog opens, treat local state as a fresh snapshot from the org.
    // This avoids showing stale defaults like "1" when the org has a different threshold.
    didUserEditRef.current = false;
    lastSyncedOrgStateKeyRef.current = null;
    setErrorMessage(null);
  }, [open]);

  // Keep local selection aligned with current org admins.
  useEffect(() => {
    // Don't clobber in-progress edits while the dialog is open.
    if (open && didUserEditRef.current) return;

    // Avoid clobbering when SWR revalidates and returns new object/array instances
    // with identical contents.
    const nextKey = `${orgApprovalsNeeded}|${orgAdminUsers
      .slice()
      .sort()
      .join(',')}`;
    if (lastSyncedOrgStateKeyRef.current === nextKey) return;
    lastSyncedOrgStateKeyRef.current = nextKey;
    setTempAdminUserIds(orgAdminUsers);
    setTempApprovalsNeeded(
      Math.min(
        Math.max(1, orgApprovalsNeeded),
        Math.max(1, orgAdminUsers.length)
      )
    );
  }, [open, orgAdminUsers, orgApprovalsNeeded, tempAdminUserIds.length]);

  // Keep approvals needed within bounds (1..N)
  useEffect(() => {
    const max = Math.max(1, tempAdminUserIds.length);
    setTempApprovalsNeeded((cur) => Math.min(Math.max(1, cur), max));
  }, [tempAdminUserIds.length]);

  const memberById = useMemo(() => {
    const map = new Map<string, OrganizationMember>();
    for (const m of members) map.set(m.id, m);
    return map;
  }, [members]);

  const selectedMembers = useMemo(() => {
    const resolved: OrganizationMember[] = [];
    for (const id of tempAdminUserIds) {
      const m = memberById.get(id);
      if (m) resolved.push(m);
    }
    return resolved;
  }, [memberById, tempAdminUserIds]);

  const hasAdminSelectionChanged = useMemo(() => {
    const a = orgAdminUsers.slice().sort().join(',');
    const b = tempAdminUserIds.slice().sort().join(',');
    return a !== b;
  }, [orgAdminUsers, tempAdminUserIds]);

  const hasApprovalsNeededChanged = useMemo(() => {
    return tempApprovalsNeeded !== orgApprovalsNeeded;
  }, [orgApprovalsNeeded, tempApprovalsNeeded]);

  const saveDisabledReasons = useMemo(() => {
    const reasons: string[] = [];
    if (isCreating) reasons.push('saving');
    if (!organizationId) reasons.push('organization not loaded');
    if (tempAdminUserIds.length === 0) reasons.push('no admins selected');
    if (activeProposal) reasons.push('pending proposal exists');
    if (!hasAdminSelectionChanged && !hasApprovalsNeededChanged)
      reasons.push('no changes to propose');
    return reasons;
  }, [
    activeProposal,
    hasAdminSelectionChanged,
    hasApprovalsNeededChanged,
    isCreating,
    organizationId,
    tempAdminUserIds.length,
  ]);

  const isSaveDisabled = saveDisabledReasons.length > 0;
  const isBlockedByActiveProposal = Boolean(activeProposal);

  const toggleUser = (userId: string) => {
    didUserEditRef.current = true;
    setTempAdminUserIds((prev) => {
      const next = prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId];
      return next;
    });
  };

  const handleCancel = () => {
    setErrorMessage(null);
    if (!organizationId) {
      setTempAdminUserIds([]);
      setTempApprovalsNeeded(1);
    } else {
      setTempAdminUserIds(orgAdminUsers);
      setTempApprovalsNeeded(
        Math.min(
          Math.max(1, orgApprovalsNeeded),
          Math.max(1, orgAdminUsers.length)
        )
      );
    }
    setOpen(false);
  };

  const submit = async () => {
    setErrorMessage(null);

    if (!organizationId) return;
    if (activeProposal) {
      setErrorMessage('A pending admin-change proposal already exists.');
      return;
    }

    const current = new Set(orgAdminUsers);
    const next = new Set(tempAdminUserIds);

    const adminUserProposals = [
      ...Array.from(next)
        .filter((id) => !current.has(id))
        .map((userId) => ({ action: 'ADD' as const, userId })),
      ...Array.from(current)
        .filter((id) => !next.has(id))
        .map((userId) => ({ action: 'REMOVE' as const, userId })),
    ];

    const isThresholdChanged = tempApprovalsNeeded !== orgApprovalsNeeded;
    if (adminUserProposals.length === 0 && !isThresholdChanged) {
      setErrorMessage('No changes to propose.');
      return;
    }

    try {
      const numberOfApprovalsNeeded = isThresholdChanged
        ? tempApprovalsNeeded
        : null;
      await createAdminChangeProposal({
        organizationId,
        adminUserProposals,
        numberOfApprovalsNeeded,
      });
      await refreshActiveProposal();
      setOpen(false);
    } catch {
      setErrorMessage('Failed to create proposal.');
    }
  };

  const buttonLabel =
    selectedMembers.length === 0
      ? 'Select admins'
      : `${selectedMembers.length} admin${selectedMembers.length === 1 ? '' : 's'}`;

  const dialogTitle = isBlockedByActiveProposal
    ? 'Proposal already exists'
    : 'Choose organization admins';
  const shouldRenderDialogDescription = !isBlockedByActiveProposal;

  // When blocked by active proposal, show a short message
  // Otherwise, show the admin selector and approval threshold selector
  const dialogBody = isBlockedByActiveProposal ? (
    <div className="space-y-6 pt-2">
      <div className="text-sm text-muted-foreground">
        Please wait until the current admin privilege proposal is approved or
        rejected before creating a new proposal.
      </div>
    </div>
  ) : (
    <div className="space-y-6 py-2">
      <div className="space-y-2">
        <Label>Admins</Label>
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-between">
              <span>Choose admins ({tempAdminUserIds.length} chosen)</span>
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[520px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search people..." />
              <CommandEmpty>No results found.</CommandEmpty>
              <CommandGroup
                className="max-h-[18rem] overflow-y-auto"
                onWheel={(e) => e.stopPropagation()}
              >
                {members.map((m) => {
                  const checked = tempAdminUserIds.includes(m.id);
                  return (
                    <CommandItem
                      key={m.id}
                      value={`${m.name} ${getEmailDisplay(m.email)}`}
                      onSelect={() => toggleUser(m.id)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          checked ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <div className="flex flex-col">
                          <span>{formatName(m.name)}</span>
                          <span className="text-xs text-muted-foreground">
                            {getEmailDisplay(m.email)}
                          </span>
                        </div>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </Command>
          </PopoverContent>
        </Popover>
        {selectedMembers.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {selectedMembers.map((m) => (
              <Badge key={m.id} variant="secondary" className="text-xs">
                {formatName(m.name)}
              </Badge>
            ))}
          </div>
        ) : null}
        {isLoadingMembers ? (
          <div className="text-xs text-muted-foreground">Loading...</div>
        ) : null}
      </div>

      <div className="space-y-2">
        <Label>Approval Threshold</Label>
        <Select
          value={tempApprovalsNeeded.toString()}
          onValueChange={(v) => {
            didUserEditRef.current = true;
            setTempApprovalsNeeded(parseInt(v));
          }}
          disabled={tempAdminUserIds.length === 0}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select approvals needed" />
          </SelectTrigger>
          <SelectContent>
            {Array.from(
              { length: Math.max(1, tempAdminUserIds.length) },
              (_, i) => i + 1
            ).map((n) => (
              <SelectItem key={n} value={n.toString()}>
                {n} of {Math.max(1, tempAdminUserIds.length)} admin
                {Math.max(1, tempAdminUserIds.length) === 1 ? '' : 's'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {errorMessage ? (
        <div className="text-sm text-rose-600">{errorMessage}</div>
      ) : null}
    </div>
  );

  // When blocked by active proposal, show a close button
  // Otherwise, show cancel and save buttons
  const dialogFooterActions = isBlockedByActiveProposal ? (
    <Button onClick={() => setOpen(false)}>Close</Button>
  ) : (
    <>
      <Button variant="outline" onClick={handleCancel}>
        Cancel
      </Button>
      <Button onClick={submit} disabled={isSaveDisabled}>
        Save
      </Button>
    </>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          role="button"
          className={cn(
            'w-full max-w-md justify-between text-left h-auto min-h-[2.5rem] py-2',
            'border-input',
            selectedMembers.length === 0 && 'text-muted-foreground'
          )}
          disabled={isLoadingOrg}
        >
          <div className="flex flex-col items-start gap-1">
            <span>{buttonLabel}</span>
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          {/* Only render description when not blocked by active proposal */}
          {shouldRenderDialogDescription ? (
            <DialogDescription>
              Set who can manage admin-privilege for your organization.
            </DialogDescription>
          ) : null}
        </DialogHeader>
        {dialogBody}

        <DialogFooter>{dialogFooterActions}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
